import React, { useState } from 'react'
import { Button } from '@wove/web-ui'
import { Plus, RefreshCw, AlertTriangle, Database } from 'lucide-react'
import { toast } from 'sonner'
import CustomFieldMappingsTable from './CustomFieldMappingsTable'
import CustomFieldMappingForm from './CustomFieldMappingForm'
import { useCustomFieldMappings, useCreateCustomFieldMapping, useUpdateCustomFieldMapping, useDeleteCustomFieldMapping, CustomFieldMapping, CreateCustomFieldMappingRequest, UpdateCustomFieldMappingRequest } from '@wove/api'

interface CustomFieldMappingsProps {
  integrationId?: string
}

export default function CustomFieldMappings({ integrationId: providedIntegrationId }: CustomFieldMappingsProps) {
  const [showForm, setShowForm] = useState(false)
  const [editingMapping, setEditingMapping] = useState<CustomFieldMapping | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<CustomFieldMapping | null>(null)

  const {
    data: mappings = [],
    isLoading,
    error,
    refetch,
  } = useCustomFieldMappings(providedIntegrationId)

  const createMappingMutation = useCreateCustomFieldMapping(providedIntegrationId)
  const updateMappingMutation = useUpdateCustomFieldMapping(providedIntegrationId)
  const deleteMappingMutation = useDeleteCustomFieldMapping(providedIntegrationId)

  const handleCreateMapping = async (mappingData: CreateCustomFieldMappingRequest) => {
    try {
      await createMappingMutation.mutateAsync(mappingData)
      setShowForm(false)
      toast.success('Custom field mapping created successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to create custom field mapping')
    }
  }

  const handleUpdateMapping = async (mappingData: UpdateCustomFieldMappingRequest) => {
    if (!editingMapping) return
    try {
      await updateMappingMutation.mutateAsync({ id: editingMapping.id, data: mappingData })
      setEditingMapping(null)
      setShowForm(false)
      toast.success('Custom field mapping updated successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to update custom field mapping')
    }
  }

  const handleDeleteMapping = async (mapping: CustomFieldMapping) => {
    try {
      await deleteMappingMutation.mutateAsync(mapping.id)
      setShowDeleteConfirm(null)
      toast.success('Custom field mapping deleted successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to delete custom field mapping')
    }
  }

  const openCreateForm = () => {
    setEditingMapping(null)
    setShowForm(true)
  }

  const openEditForm = (mapping: CustomFieldMapping) => {
    setEditingMapping(mapping)
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingMapping(null)
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading custom field mappings
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="text-red-700 border-red-300 hover:bg-red-100"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Database className="h-5 w-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-medium">Custom Field Mappings</h3>
          </div>
          <span className="text-sm text-gray-500">
            {mappings.length} mapping{mappings.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={openCreateForm}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Mapping
          </Button>
        </div>
      </div>

      {/* Description */}
      <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
        <div className="flex items-start">
          <Database className="h-5 w-5 text-purple-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-purple-800">
              About Custom Field Mappings
            </h3>
            <p className="mt-1 text-sm text-purple-700">
              Custom field mappings store actual field values for this integration. Each mapping represents a specific custom field with its corresponding value and data type.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Field Mappings Table */}
      <CustomFieldMappingsTable
        mappings={mappings}
        onEdit={openEditForm}
        onDelete={(mapping) => setShowDeleteConfirm(mapping)}
        isLoading={isLoading}
      />

      {/* Create/Edit Form Modal */}
      {showForm && (
        <CustomFieldMappingForm
          mapping={editingMapping || undefined}
          onSubmit={(data) => {
            if (editingMapping) {
              handleUpdateMapping(data as UpdateCustomFieldMappingRequest)
            } else {
              handleCreateMapping(data as CreateCustomFieldMappingRequest)
            }
          }}
          onCancel={closeForm}
          isSubmitting={createMappingMutation.isPending || updateMappingMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full m-4">
            <div className="p-6">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Custom Field Mapping
                </h3>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete the mapping for "{showDeleteConfirm.key}"? 
                  This action cannot be undone.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  disabled={deleteMappingMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteMapping(showDeleteConfirm)}
                  disabled={deleteMappingMutation.isPending}
                >
                  {deleteMappingMutation.isPending ? 'Deleting...' : 'Delete Mapping'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}